// packages/web/src/api/subscription.ts
/**
 * Subscription API client — read + create + cancel the student monthly card.
 *
 * 19BN Phase D D1: GET /account/subscription was missing (spec §7.2 listed it);
 * create/cancel already existed as POST endpoints. This client exposes all
 * three so account-slice.refreshSubscription + the subscription tab can talk
 * to the backend through one typed surface.
 *
 * 401 contract: read returns null for anonymous / expired sessions (poll-like
 * behavior — the tab treats it as "not loaded"). create/cancel throw on 401
 * (user-initiated writes; a 401 is a real error).
 *
 * Spec: docs/architecture/account-system-design.md §2.4, §7.2, §9.4
 */

import { INGEST_URL, IngestApiError, accountHeaders } from '../lib/api-client.js';
import type { IngestErrorResponse } from '../lib/api-client.js';

export interface SubscriptionState {
  active: boolean;
  plan?: string;
  status?: string;
  periodStart?: number;
  periodEnd?: number;
  autoRenew?: boolean;
  cancelAt?: number | null;
}

async function getSubscription<T>(path: string): Promise<T | null> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown' }) as IngestErrorResponse);
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}

async function postSubscription<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'POST',
    headers: accountHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown' }) as IngestErrorResponse);
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}

export const subscriptionApi = {
  /** Read the current user's subscription state. Null on 401. */
  get: () => getSubscription<SubscriptionState>('/account/subscription'),
  /** Open a student monthly subscription. Returns { ok, subscriptionId, periodEnd }. */
  create: () => postSubscription<{ ok: true; subscriptionId: string; periodEnd: number }>('/subscription/create', {}),
  /** Cancel the active subscription. Idempotent. */
  cancel: () => postSubscription<{ ok: true }>('/subscription/cancel', {}),
};
